/* eslint-disable eqeqeq */
/* eslint-disable no-underscore-dangle */
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';

import { InputStatus } from '../../../../directive/input-mask/input-mask-processing.base';
import { maskInput } from '../../../../directive/input-mask/mask.helper';
import { getValidationObject } from '../../../../helper/validation-converter/validation-converter';
import { CoreBuildingBlockControlBaseComponent } from '../../shared/building-block-control-base.component';
import { CoreBuildingBlock } from '../../shared/building-block.decorator';

@CoreBuildingBlock('input')
@Component({
  selector: 'cu-input',
  templateUrl: './input.component.html'
})
export class InputComponent extends CoreBuildingBlockControlBaseComponent implements OnDestroy, OnInit {
  /** element reference input  */
  @ViewChild('input', { static: true }) input!: ElementRef<HTMLInputElement>;
  private subscription = Subscription.EMPTY;

  /** input status */
  private lastState: InputStatus;
  /** masked value */
  private _maskedValue: string;
  /** value adjusting */
  private valueAdjusting = false;
  /** input type string */
  public inputType = 'text';
  /** input autocomplete boolean */
  public inputAutocomplete = 'off';

  /** value change callback */
  public readonly onValueChange = (): void => {
    const fieldValue = this.field?.data?.value?.toString();
    // When Save Application Data to Session Storage Feature Flag is turned ON
    // Removing the logic to patch empty values onLoad for input fields
    if (fieldValue?.trim().length > 0) {
      this.input.nativeElement.value = fieldValue;
      this.processMask();
    }
  };

  public ngOnDestroy(): void {
    if (this.isAutoAddress && this.subscription) {
      this.addressService.unsubscribe(this.subscription);
    }

    if (this.field && this.field.focused) {
      this.field.focused = false;
      this.field.data.saveUnlock();
    }

    this.field.data.unsubscribe(this.onValueChange);
  }

  get isAutoAddress() {
    return this.field?.config?.autoAddress;
  }

  public ngOnInit(): void {
    if (this.isAutoAddress) {
      this.subscription = this.addressService.autocomplete(this.field, this.input.nativeElement);
    }

    setTimeout(this.onValueChange, 0);
    this.field.data.onChange(this.onValueChange);
    if (!this.autoCompleteEnabled) {
      this.inputType = 'search';
      this.inputAutocomplete = 'something-new';
    }
    super.ngOnInit();
  }

  /**
   * Getter / setter for masked value. Both get & set trigger mask processing
   */
  public get maskedValue(): string {
    return this._maskedValue;
  }

  public set maskedValue(str: string) {
    this._maskedValue = str;
    this.processMask();
  }

  public processMask(setSelection = true): void {
    this.addPrefixtoDate();
    this.removePrefixZero();
    const mask = maskInput(this.field.config.mask, this.readState(), this.lastState, getValidationObject(this.field));
    if (mask.changed) {
      this.valueAdjusting = true;
      this.input.nativeElement.value = mask.processed.value;
      if (setSelection && this.input.nativeElement.value) {
        this.input.nativeElement.setSelectionRange(mask.processed.selection[0], mask.processed.selection[1]);
      }
      this.field.data.value = mask.processed.value; // TODO: change to reverse mask
      this.valueAdjusting = false;
    }
    this.lastState = this.readState();
    this._maskedValue = mask.processed.value;
    if (this.input.nativeElement.getAttribute('type') === 'search' && this.input.nativeElement.hasAttribute('autocomplete')) {
      this.input.nativeElement.removeAttribute('autocomplete');
      this.input.nativeElement.setAttribute('autocomplete', 'something-new');
    }
  }

  private readState(): InputStatus {
    const domElement = this.input.nativeElement;
    return {
      focused: this.focused,
      selection: [domElement.selectionStart, domElement.selectionEnd],
      value: this.input.nativeElement.value
    };
  }

  /**
   * Creating a function to place the cursor
         at the end
   * @param elm
   */
  public setCurorToTheStart(elm): void {
    const len = elm.value.length;
    elm.focus();
    elm.setSelectionRange(len, 0);
  }

  /**
   * Clears all the prefix($)/suffix(%) values when the input field is empty
   */
  public clearPrefixSuffix(): void {
    setTimeout(() => {
      if (!this._maskedValue) {
        this.input.nativeElement.value = '';
      }
    }, 90);
  }

  /**
   * Blocks special chars and allow alphabets and numbers only
   * @param e
   */
  public blockSpecialChar(e) {
    if (this.field.config.label === 'ZIP/Postal code') {
      const valid = (e.which >= 48 && e.which <= 57) || (e.which >= 65 && e.which <= 90) || (e.which >= 97 && e.which <= 122);
      if (!valid) {
        e.preventDefault();
      }
    }
  }

  /**  TODO: revisit / refactor */
  /*
   * Adds prefix(0) when the input field having value && focus is false
   */
  private addPrefixtoDate(): void {
    // Bug 60725: ECP MTG | For date fields when entered one digit month 'Zero' is not adding automatically during masking
    const state: InputStatus = this.readState();
    if (this.field.config.mask == 'date' && !state.focused && state.value) {
      if (state.value.length == 6) {
        this.input.nativeElement.value = this.addStr(state.value, 0, '0');
      } else if (state.value.length == 8) {
        this.input.nativeElement.value = this.addStr(this.addStr(state.value, 0, '0'), 2, '0');
      } else if (state.value.length == 9) {
        this.input.nativeElement.value =
          parseInt(state.value.slice(0, 2)) <= 12 ? this.addStr(state.value, 2, '0') : this.addStr(state.value, 0, '0');
      }
    }
  }

  private addStr(str: string, index: number, stringToAdd: string): string {
    return str.substring(0, index) + stringToAdd + str.substring(index, str.length);
  }

  /** TODO: Need to be refactored */
  /*
   * Remove prefix(0) when the input field having value
   */
  private removePrefixZero(): void {
    // Bug 128954: OAO 2.0 | Business membership percent ownership allows 0 as first numeric character
    const state: InputStatus = this.readState();
    if (this.field.config.mask == 'percent' && !state.focused && state.value && state.value.charAt(0) === '0') {
      this.input.nativeElement.value = parseFloat(state.value) == 0 ? `%` : parseFloat(state.value) + `%`;
    }
  }

  public doFocus() {
    this.focused = true;
    this.processMask();
    if (this.field.config.inputMode === 'numeric' && this.field.config.clearZero && this.field.data.valueNumber === 0) {
      this.field.data.value = '';
    }
  }

  public doBlur() {
    this.focused = false;
    this.processMask(false);
    if (
      this.field.config.inputMode === 'numeric' &&
      this.field.config.clearZero &&
      (this.field.data.value === null || this.field.data.value === '')
    ) {
      this.field.data.value = '0';
    }
  }
}
